import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { trackEvent } from "@/lib/analytics";
import { expenseSchema } from "@/lib/expenseValidation";
import { ZodError } from "zod";
import { useLanguage } from "@/contexts/LanguageContext";
import { CURRENCY_SYMBOLS, EXCHANGE_RATES } from "@/lib/currencyConversion";
import { useGamification } from "@/contexts/GamificationContext";

// Derive supported currencies from the exchange rates to keep in sync
const SUPPORTED_CURRENCIES = Object.keys(EXCHANGE_RATES) as Array<keyof typeof EXCHANGE_RATES>;

interface Expense {
  id: string;
  amount: number;
  merchant: string;
  category: string;
  expense_date: string;
  note: string | null;
  currency?: string;
  tag?: string | null;
}

interface ExpenseEditDialogProps {
  expense: Expense | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: () => void;
}

const COMMON_CATEGORIES = ['Food', 'Transport', 'Shopping', 'Fun', 'Health', 'Housing', 'Utilities', 'Bills/Other'];

export const ExpenseEditDialog = ({ expense, open, onOpenChange, onUpdate }: ExpenseEditDialogProps) => {
  const { t } = useLanguage();
  const { settings } = useGamification();
  const [amount, setAmount] = useState(expense?.amount.toString() || "");
  const [merchant, setMerchant] = useState(expense?.merchant || "");
  const [note, setNote] = useState(expense?.note || "");
  const [expenseDate, setExpenseDate] = useState(expense?.expense_date || new Date().toISOString().split('T')[0]);
  const [category, setCategory] = useState(expense?.category || "Bills/Other");
  const [currency, setCurrency] = useState(expense?.currency || "EUR");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (expense) {
      setAmount(expense.amount.toString());
      setMerchant(expense.merchant);
      setNote(expense.note || "");
      setExpenseDate(expense.expense_date);
      setCategory(expense.category);
      setCurrency(expense.currency || "EUR");
    }
  }, [expense]);

  const handleSave = async () => {
    if (!expense) return;
    
    setIsSaving(true);
    try {
      // Validate only the fields being updated
      const parsedAmount = parseFloat(amount);
      const validatedMerchant = expenseSchema.shape.merchant.parse(merchant);
      const validatedNote = note ? expenseSchema.shape.note.parse(note) : null;
      
      // Additional amount validation
      if (isNaN(parsedAmount) || parsedAmount <= 0 || parsedAmount > 1000000) {
        throw new Error('Invalid amount');
      }

      const { error } = await supabase
        .from('expenses')
        .update({
          amount: parsedAmount,
          merchant: validatedMerchant,
          note: validatedNote,
          expense_date: expenseDate,
          category: category,
          currency: currency,
        })
        .eq('id', expense.id);

      if (error) throw error;

      trackEvent({ name: 'edit_saved' });
      
      const calmMode = settings.hideRewards;
      
      // Fetch user progress for gamification toast
      const { data: { user } } = await supabase.auth.getUser();
      let streakText = "";
      if (user && !calmMode) {
        try {
          const { getUserProgress } = await import('@/lib/gamification');
          const progress = await getUserProgress(user.id);
          if (progress) {
            streakText = ` · ${progress.current_streak}-day streak 🔥`;
          }
        } catch (gamError) {
          console.error('Gamification error:', gamError);
        }
      }
      
      if (!calmMode) {
        toast({
          title: t('expense.updated'),
          description: `${merchant}${streakText}`,
        });
      } else {
        toast({
          title: t('expense.updated'),
        });
      }

      onUpdate();
      onOpenChange(false);
    } catch (error: any) {
      if (error instanceof ZodError) {
        toast({
          title: t('common.invalidInput'),
          description: error.errors[0].message,
          variant: "destructive",
        });
      } else {
        toast({
          title: t('common.error'),
          description: error.message,
          variant: "destructive",
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!expense) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('expense.edit')}</SheetTitle>
        </SheetHeader>
        
        <div className="space-y-4 py-6">
          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="amount">{t('expense.amount')}</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="h-12 text-lg"
            />
          </div>

          {/* Currency */}
          <div className="space-y-2">
            <Label>{t('expense.currency')}</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger className="h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPPORTED_CURRENCIES.map((curr) => (
                  <SelectItem key={curr} value={curr}>
                    {CURRENCY_SYMBOLS[curr as keyof typeof CURRENCY_SYMBOLS] || curr} {curr}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date */}
          <div className="space-y-2">
            <Label htmlFor="date">{t('expense.date')}</Label>
            <Input
              id="date"
              type="date"
              value={expenseDate}
              onChange={(e) => setExpenseDate(e.target.value)}
              className="h-12"
            />
          </div>

          {/* Merchant */}
          <div className="space-y-2">
            <Label htmlFor="merchant">{t('expense.merchant')}</Label>
            <Input
              id="merchant"
              value={merchant}
              onChange={(e) => setMerchant(e.target.value)}
              className="h-12"
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label>{t('expense.category')}</Label>
            <div className="flex flex-wrap gap-2">
              {COMMON_CATEGORIES.map((cat) => (
                <Button
                  key={cat}
                  variant={category === cat ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCategory(cat)}
                  className="min-h-[36px]"
                >
                  {cat}
                </Button>
              ))}
            </div>
          </div>

          {/* Note */}
          <div className="space-y-2">
            <Label htmlFor="note">{t('expense.noteOptional')}</Label>
            <Input
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('expense.addNote')}
              className="h-12"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-2 pt-4">
            <Button 
              onClick={handleSave} 
              disabled={isSaving}
              className="flex-1 min-h-[44px]"
            >
              {isSaving ? t('common.saving') : t('common.save')}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="min-h-[44px]"
              disabled={isSaving}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
